/**
 * Normalizes array fields from Supabase JSONB to ensure proper string formatting
 * Handles both arrays of strings and arrays of objects
 * @param {any} value - The value to normalize (could be array, JSONB, string, etc.)
 * @returns {string[]} - Array of strings
 */
export const normalizeArrayField = (value) => {
  if (!value) return [];
  
  // If it's already an array
  if (Array.isArray(value)) {
    return value
      .map(item => {
        if (item === null || item === undefined) return '';
        
        // Handle objects
        if (typeof item === 'object') {
          // Check if object has a name/label property
          if (item.name) return item.name;
          if (item.label) return item.label;
          if (item.type) return item.type;
          // Fallback to JSON string representation
          try {
            return JSON.stringify(item);
          } catch {
            return String(item);
          }
        }
        
        // Handle primitives
        return String(item);
      })
      .filter(item => item !== '' && item !== '{}');
  }
  
  // If it's a string, return as array with single element
  if (typeof value === 'string') {
    return value ? [value] : [];
  }
  
  // If it's an object but not an array, try to extract name
  if (typeof value === 'object') {
    if (value.name) return [value.name];
    if (value.label) return [value.label];
    if (value.type) return [value.type];
  }
  
  return [];
};

/**
 * Formats array of strings or objects into a display string
 * @param {any} value - The value to format
 * @param {string} separator - The separator to use between items (default: ', ')
 * @param {string} fallback - The fallback text if no items (default: 'N/A')
 * @returns {string} - Formatted display string
 */
export const formatArrayForDisplay = (value, separator = ', ', fallback = 'N/A') => {
  const normalized = normalizeArrayField(value);
  return normalized.length > 0 ? normalized.join(separator) : fallback;
};
